// Installation and notifications, per studio scope.
import { useEffect, useState } from 'react';

export const isStandalone = () =>
  window.matchMedia?.('(display-mode: standalone)').matches || (navigator as Navigator & { standalone?: boolean }).standalone === true;

export const isIOS = () =>
  /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

/** One service worker script, registered separately for each studio scope. */
export async function registerStudioWorker(slug: string) {
  if (!('serviceWorker' in navigator) || import.meta.env.DEV) return null;
  try {
    return await navigator.serviceWorker.register('/sw.js', { scope: `/s/${slug}/`, updateViaCache: 'none' });
  } catch (e) {
    console.warn('service worker registration failed', e);
    return null;
  }
}

export type PushSupport =
  | { ok: true }
  | { ok: false; reason: 'no_api' | 'ios_needs_install' | 'denied' | 'no_key' | 'no_worker' };

/** Honest capability check: iPhone gets push only from an installed app. */
export function pushSupport(): PushSupport {
  if (!import.meta.env.VITE_VAPID_PUBLIC_KEY) return { ok: false, reason: 'no_key' };
  if (isIOS() && !isStandalone()) return { ok: false, reason: 'ios_needs_install' };
  if (!('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window)) return { ok: false, reason: 'no_api' };
  if (Notification.permission === 'denied') return { ok: false, reason: 'denied' };
  return { ok: true };
}

const b64ToBytes = (b64: string) => {
  const pad = '='.repeat((4 - (b64.length % 4)) % 4);
  const raw = atob((b64 + pad).replace(/-/g, '+').replace(/_/g, '/'));
  return Uint8Array.from(raw, (ch) => ch.charCodeAt(0));
};
const bytesToB64 = (buf: ArrayBuffer | null) =>
  buf ? btoa(String.fromCharCode(...new Uint8Array(buf))).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '') : '';

export async function subscribePush(slug: string) {
  const reg = (await navigator.serviceWorker.getRegistration(`/s/${slug}/`)) ?? (await registerStudioWorker(slug));
  if (!reg) throw new Error('no_worker');
  await navigator.serviceWorker.ready;
  const perm = await Notification.requestPermission();
  if (perm !== 'granted') throw new Error('denied');
  const sub =
    (await reg.pushManager.getSubscription()) ??
    (await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: b64ToBytes(import.meta.env.VITE_VAPID_PUBLIC_KEY!) }));
  return { endpoint: sub.endpoint, p256dh: bytesToB64(sub.getKey('p256dh')), auth: bytesToB64(sub.getKey('auth')) };
}

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}
let deferred: BeforeInstallPromptEvent | null = null;
const listeners = new Set<() => void>();
if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    deferred = e as BeforeInstallPromptEvent;
    listeners.forEach((l) => l());
  });
  window.addEventListener('appinstalled', () => {
    deferred = null;
    listeners.forEach((l) => l());
  });
}

/** 'prompt' (Android/desktop Chrome), 'ios' (show Share -> На экран «Домой»), 'installed', 'none'. */
export function useInstall() {
  const [, force] = useState(0);
  useEffect(() => {
    const l = () => force((x) => x + 1);
    listeners.add(l);
    return () => void listeners.delete(l);
  }, []);
  const mode: 'prompt' | 'ios' | 'installed' | 'none' = isStandalone() ? 'installed' : deferred ? 'prompt' : isIOS() ? 'ios' : 'none';
  return {
    mode,
    async install() {
      if (!deferred) return false;
      await deferred.prompt();
      const r = await deferred.userChoice;
      deferred = null;
      force((x) => x + 1);
      return r.outcome === 'accepted';
    },
  };
}
